/**
 * Vendor Application saga functions
 */
import {
  all,
  call,
  put,
  take,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';
import Actions from './actions';
import Constants from '../../constants';
import UserApi from '../../Lib/Api/user';
import FilesAPI from '../../Lib/Api/files';
import { handleError } from '../../Lib/Utils/saga-utils';

const { Vendor } = Constants;
const {
  createVendorNetSuiteSuccess,
  createVendorNetSuiteError,
  createVendorContactError,
  createVendorContactSuccess,
  updateVendorSuccess,
  updateVendorError,
  updateVendorFileProgress,
  getPublicFilesError,
  getPublicFilesSuccess,
  getVendorSuccess,
  getVendorError,
  updateVendorNetSuiteSuccess,
  updateVendorNetSuiteError,
  updateVendorContactError,
  updateVendorContactSuccess,
  removeVendorContactError,
  removeVendorContactSuccess,
  submitVendorError,
  submitVendorSuccess,
} = Actions;

/**
 * Creates vendor in Netsuite and dispatches success or error
 *
 * @param {string} userId the user id
 */
export function* createVendorNetSuite({ userId, resolve, reject }) {
  try {
    const response = yield call(UserApi.createVendorNetSuite, userId);
    yield put(createVendorNetSuiteSuccess(response));
    yield call(resolve, { response });
  } catch (err) {
    yield handleError(createVendorNetSuiteError, err);
    yield call(reject, err);
  }
}

/**
 * Updates vendor in Netsuite and dispatches success or error
 *
 * @param {string} userId the user id
 * @param {object} vendor the user id
 */
export function* updateVendorNetSuite({ userId, vendor, resolve, reject }) {
  try {
    const response = yield call(UserApi.updateVendorNetSuite, userId, vendor);
    yield call(UserApi.updateVendor, userId, vendor, false);
    yield put(updateVendorNetSuiteSuccess(response));
    yield call(resolve, response);
  } catch (err) {
    yield handleError(updateVendorNetSuiteError, err, reject);
  }
}

/**
 * Updates vendor and dispatches success or error
 *
 *  @param {object} action:
 *  {
 *    @property {string} userId: the user id
 *    @property {object} formData:the key value pair for updating vendor fields
 *    @property {boolean} fileUpload: indicates whether file is uploaded or not
 *  }
 */
export function* updateVendor({
  userId,
  formData,
  fileUpload,
  resolve,
  reject,
}) {
  try {
    const chan = yield call(UserApi.updateVendor, userId, formData, fileUpload);
    if (fileUpload) {
      while (true) {
        const event = yield take(chan);
        if (event.progressEvent) {
          yield put(
            updateVendorFileProgress(event.progressEvent, event.fileName),
          );
        } else if (event.data) {
          yield put(updateVendorSuccess(event.data));
          yield call(resolve, event.data);
        }
      }
    } else {
      yield put(updateVendorSuccess(chan));
      yield call(resolve, chan);
    }
  } catch (err) {
    yield handleError(updateVendorError, err, reject);
  }
}

/**
 * Updates vendor banking and dispatches success or error
 *
 *  @param {object} action:
 *  {
 *    @property {string} userId: the user id
 *    @property {object} formData:the key value pair for updating banking vendor fields
 *  }
 */
export function* updateVendorBanking({ userId, formData, resolve, reject }) {
  try {
    const chan = yield call(UserApi.updateVendorBanking, userId, formData);
    yield put(updateVendorSuccess(chan));
    yield call(resolve, chan);
  } catch (err) {
    yield handleError(updateVendorError, err, reject);
  }
}

/**
 * Gets public files and dispatches success or error
 *
 */
export function* getPublicFiles({ getType }) {
  try {
    const response = yield call(FilesAPI.getPublicFiles, getType);
    yield put(getPublicFilesSuccess(response));
  } catch (err) {
    yield handleError(getPublicFilesError, err);
  }
}

/**
 * Gets vendor and dispatches success
 *
 * @param {string} userId the user id
 */
export function* getVendor({ userId }) {
  try {
    const response = yield call(UserApi.getVendor, userId);
    yield put(getVendorSuccess(response));
  } catch (err) {
    yield handleError(getVendorError, err);
  }
}

/**
 * Creates the vendor contact, dispatches success or error
 *
 * @param action {
 *  {integer} userId,
 *  {object} contact - contact object - contains first_name, last_name,
 *            email, phone, title
 * }
 */
export function* createVendorContact({
  userId,
  contact,
  reInstanciateDeletedUser,
}) {
  try {
    const { vendor } = yield call(
      UserApi.createVendorContact,
      userId,
      contact,
      reInstanciateDeletedUser,
    );
    yield put(createVendorContactSuccess(vendor));
  } catch (err) {
    yield handleError(createVendorContactError, err);
  }
}

/**
 *
 * Updates the vendor contact, dispatches success or error
 *
 * @param action {
 *  {integer} userId,
 *  {integer} contactId - the vendor contact id
 *  {object} updates - updates object
 * }
 */
export function* updateVendorContact({ userId, contactId, updates }) {
  try {
    const { vendor } = yield call(
      UserApi.updateVendorContact,
      userId,
      contactId,
      updates,
    );
    yield put(updateVendorContactSuccess(vendor));
  } catch (err) {
    yield handleError(updateVendorContactError, err);
  }
}

/**
 *
 * Removes the vendor contact, dispatches success or error
 *
 * @param action {
 *  {integer} userId,
 *  {integer} contactId - the vendor contact id
 * }
 */
export function* removeVendorContact({ userId, contactId }) {
  try {
    const { vendor } = yield call(
      UserApi.removeVendorContact,
      userId,
      contactId,
    );
    yield put(removeVendorContactSuccess(vendor));
  } catch (err) {
    yield handleError(removeVendorContactError, err);
  }
}

/**
 *
 * Removes the vendor contact, dispatches success or error
 *
 * @param action {
 *  {integer} userId,
 *  {integer} contactId - the vendor contact id
 * }
 */
export function* submitVendor({ userId, resolve, reject }) {
  try {
    const { vendor } = yield call(UserApi.submitVendor, userId);
    yield put(submitVendorSuccess(vendor));
    yield call(resolve, { vendor });
  } catch (err) {
    yield handleError(submitVendorError, err);
    yield call(reject, err);
  }
}

/**
 * Gets the response from the latest request to create vendor in Netsuite,
 * get vendor, and update vendor in Netsuite.
 * Allows multiple requests to update vendors.
 */
function* vendorApplicationSaga() {
  yield all([
    yield takeLatest(Vendor.CREATE_VENDOR_NETSUITE, createVendorNetSuite),
    yield takeEvery(Vendor.UPDATE_VENDOR, updateVendor),
    yield takeLatest(Vendor.UPDATE_VENDOR_BANKING, updateVendorBanking),
    yield takeEvery(Vendor.GET_PUBLIC_FILES, getPublicFiles),
    yield takeLatest(Vendor.GET_VENDOR, getVendor),
    yield takeLatest(Vendor.UPDATE_VENDOR_NETSUITE, updateVendorNetSuite),
    yield takeLatest(Vendor.SUBMIT_VENDOR, submitVendor),
  ]);
}

export default vendorApplicationSaga;
